/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { AddToCartBehavior } from '@msdyn365-commerce/components';
import MsDyn365, { getUrlSync } from '@msdyn365-commerce/core';
import { ICartIconViewProps, ICartViewProps, IFlyoutCartLineItemViewProps } from '@msdyn365-commerce-modules/cart';
import { Button, getPayloadObject, INodeProps, KeyCodes, Modal, Node, onTelemetryClick } from '@msdyn365-commerce-modules/utilities';
import classnames from 'classnames';
import React from 'react';

import { ICartIconProps as ICartIconExtensionProps } from '../definition-extensions/cart-icon.ext.props.autogenerated';

import { getCategoriesAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/CategoriesDataActions.g';

import { IAppSettings, IActionContext } from '@msdyn365-commerce/core-internal';
import getIsolatedAttributesForSelectedVariantAction, {
    IsolatedAttributesForSelectedVariantInput
} from '../../../actions/get-isolated-attributes-for-selected-variant';
import { getAttributeByTechnicalName } from '../../../common/utilities/attributes-helpers';

/**
 * ICartIconViewState: Interface for Cart Icon View State.
 */
interface ICartIconViewState {
    isModalOpen: boolean;
}

/**
 * Fetch sales unit attributes for all cart lines in flyout.
 * @param cartLines - The flyout cart line view props.
 * @param actionContext - Action context for data actions.
 * @param channelId - Channel ID.
 * @param catalogId - Catalog ID.
 * @param appSettings - App settings for attribute helpers.
 * @returns Promise with array of sales units indexed by cart line index.
 */
const fetchAllFlyoutSalesUnits = async (
    cartLines: IFlyoutCartLineItemViewProps[],
    actionContext: IActionContext,
    channelId: number,
    catalogId: number,
    appSettings: IAppSettings
): Promise<{ [index: number]: string }> => {
    const salesUnits: { [index: number]: string } = {};

    const promises = cartLines.map(async (cartLine, index) => {
        const product = cartLine.data?.product;
        if (!product?.RecordId) {
            return;
        }

        try {
            const input = new IsolatedAttributesForSelectedVariantInput(product.RecordId, channelId, product, catalogId);
            const attributes = await getIsolatedAttributesForSelectedVariantAction(input, actionContext);

            const wbsSalesUnitAttribute = getAttributeByTechnicalName(attributes, 'WBS_SALES_UNIT', appSettings);
            const salesUnit = wbsSalesUnitAttribute?.attribute.TextValue || '';

            if (salesUnit) {
                salesUnits[index] = salesUnit;
                // console.log(`✅ [fetchAllFlyoutSalesUnits] Fetched sales unit for flyout cart line ${index}:`, salesUnit);
            }
        } catch (error) {
            // console.error(`❌ [fetchAllFlyoutSalesUnits] Error fetching attributes for flyout cart line ${index}:`, error);
        }
    });

    await Promise.all(promises);
    // console.log('📦 [fetchAllFlyoutSalesUnits] All flyout sales units fetched:', salesUnits);
    return salesUnits;
};

/**
 * Render Cart lines.
 * @param cartLines - Flyout cart line view props.
 * @param props - Cart icon view props.
 * @param salesUnits - Sales units indexed by cart line index.
 * @returns JSX Element.
 */
const renderCartlines = (
    cartLines: IFlyoutCartLineItemViewProps[] | undefined,
    props: ICartIconViewProps,
    salesUnits?: { [index: number]: string }
): JSX.Element | null => {
    if (!cartLines) {
        props.context.telemetry.error('Cartlines content is empty, module wont render');
        return null;
    }

    // Générer les règles CSS dynamiques pour les sales units
    const generateFlyoutSalesUnitStyles = () => {
        if (!salesUnits || Object.keys(salesUnits).length === 0) {
            return null;
        }

        const cssRules = Object.entries(salesUnits)
            .map(([index, salesUnit]) => {
                const escapedSalesUnit = salesUnit.replace(/"/g, '\\"');
                return `
                    .msc-cart-line__flyout-item--${index} .msc-cart-line__product-unit-of-measure span::after {
                        content: " - ${escapedSalesUnit}";
                        display: inline;
                    }
                `;
            })
            .join('\n');

        return <style dangerouslySetInnerHTML={{ __html: cssRules }} />;
    };

    const cartLineElements = cartLines.map((cartLine, index) => {
        const cartLineIndex = index;
        return (
            <Node {...props.miniCartItemWrapper} key={cartLineIndex}>
                <Node
                    className={classnames(
                        cartLine.storeLocation ? 'msc-cart-line__set-net-price' : '',
                        `msc-cart-line__flyout-item--${cartLineIndex}`
                    )}
                >
                    {cartLine.cartline}
                </Node>
                {cartLine.storeLocation}
                {cartLine.remove}
            </Node>
        );
    });

    return (
        <>
            {generateFlyoutSalesUnitStyles()}
            {cartLineElements}
        </>
    );

    // return cartLines.map((cartLine, index) => {
    //     const cartLineIndex = index;
    //     return (
    //         <Node {...props.miniCartItemWrapper} key={cartLineIndex}>
    //             <Node className={classnames(cartLine.storeLocation ? 'msc-cart-line__set-net-price' : '')}>{cartLine.cartline}</Node>
    //             {cartLine.storeLocation}
    //             {cartLine.remove}
    //         </Node>
    //     );
    // });
};

/**
 * Initiate total price.
 * @param props - Cart icon view props.
 * @returns JSX Element.
 */
const renderTotalPrice = (props: ICartIconViewProps & ICartIconExtensionProps<{}>): JSX.Element | null => {
    const cart = props.data.cart.result ?? undefined;
    const price = cart && !cart.hasInvoiceLine && (cart.cart.TotalAmount || undefined);
    const totalPrice = price
        ? props.context.cultureFormatter.formatCurrency(price)
        : props.context.cultureFormatter.formatCurrency(props.resources.emptyPrice);
    const priceString = price ? props.resources.totalPriceFormatString.replace('{price}', '') : '';
    return cart && !cart.hasInvoiceLine ? (
        <div className='ms-cart-icon__subTotalText'>
            <span className='ms-cart-icon__subTotalPriceText'>
                {/* {props.resources.totalPriceFormatString} */}
                {priceString}
                <span className='ms-cart-icon__subTotalPrice'>{totalPrice}</span>
            </span>
        </div>
    ) : (
        <></>
    );
};

/**
 * Initiate subtotal message.
 * @param props - Cart icon view props.
 * @returns JSX Element.
 */
const renderSubtotalMessage = (props: ICartIconViewProps & ICartIconExtensionProps<{}>): JSX.Element | null => {
    const cart = props.data.cart.result ?? undefined;
    return cart && !cart.hasInvoiceLine ? <p className='ms-cart-icon__subtotal-message'>{props.resources.subTotalMessage}</p> : null;
};

/**
 *
 * CartIconView component.
 * @extends {React.PureComponent<ICartIconViewProps>}
 */
export class CartIconView extends React.PureComponent<
    ICartViewProps & ICartIconViewProps & ICartIconExtensionProps<{}>,
    ICartIconViewState
> {
    private isAutoDisplayTriggered: boolean;

    private readonly cartIconRef: React.RefObject<HTMLButtonElement> = React.createRef();

    private _productsRootCategory: { RecordId: number; Name: string } | null = null;

    private _flyoutSalesUnits: { [index: number]: string } = {};

    public constructor(props: ICartViewProps & ICartIconViewState & ICartIconViewProps & ICartIconExtensionProps<{}>) {
        super(props);
        this.isAutoDisplayTriggered = false;
        this.state = {
            isModalOpen: false
        };
    }

    public async componentDidMount(): Promise<void> {
        if (MsDyn365.isBrowser) {
            window.addEventListener('keydown', this._escFunction, false);

            // Charger la catégorie "Products"
            await this._loadProductsCategory();

            // Charger les sales units pour le flyout
            await this._loadFlyoutSalesUnits();
        }
    }

    public async componentDidUpdate(prevProps: ICartViewProps & ICartIconViewProps & ICartIconExtensionProps<{}>): Promise<void> {
        // Recharger les sales units si le nombre de lignes a changé
        const prevCartLinesLength = prevProps.cartlines?.length || 0;
        const currentCartLinesLength = this.props.cartlines?.length || 0;

        if (prevCartLinesLength !== currentCartLinesLength) {
            await this._loadFlyoutSalesUnits();
        }
    }

    public componentWillUnmount(): void {
        if (MsDyn365.isBrowser) {
            window.removeEventListener('keydown', this._escFunction, false);
        }
    }

    private readonly _loadFlyoutSalesUnits = async (): Promise<void> => {
        const cartLines = this.props.cartlines;

        if (!cartLines || cartLines.length === 0) {
            // console.log('⚠️ [CartIconView] No flyout cart lines to process');
            return;
        }

        const appSettings = this.props.context.app as IAppSettings;
        const actionContext = this.props.context.actionContext;
        const channelId = +actionContext.requestContext.apiSettings.channelId;
        const catalogId =
            this.props.data.catalogs?.result && this.props.data.catalogs.result.length > 0
                ? this.props.data.catalogs.result[0].RecordId
                : 0;

        // console.log('🔄 [CartIconView] Starting to fetch flyout sales units for', cartLines.length, 'cart lines');

        try {
            const salesUnits = await fetchAllFlyoutSalesUnits(cartLines, actionContext, channelId, catalogId, appSettings);
            this._flyoutSalesUnits = salesUnits;
            this.forceUpdate(); // Forcer le re-render
            // console.log('✅ [CartIconView] Flyout sales units loaded:', salesUnits);
        } catch (error) {
            // console.error('❌ [CartIconView] Error loading flyout sales units:', error);
        }
    };

    private readonly _loadProductsCategory = async (): Promise<void> => {
        try {
            const channelId = this.props.context.request.apiSettings.channelId;
            const catalogId = this.props.context.request.apiSettings.catalogId;
            // const locale = this.props.context.request.locale || 'en-US';
            // console.log('Locale detected:', locale);

            // Déterminer le nom selon la langue
            // const localizedCategoryNameByLocale: { [key: string]: string } = {
            //     'en-US': 'products',
            //     'fr': 'produits',
            //     'nl': 'producten'
            // };

            // const localizedCategoryName = localizedCategoryNameByLocale[locale.toLowerCase()] || 'products';
            // console.log('Category name to search:', localizedCategoryName);

            // Récupérer toutes les catégories
            const categories = await getCategoriesAsync({ callerContext: this.props.context.actionContext }, channelId, catalogId);
            // console.log('Categories retrieved:', categories);

            // LocalizedDescription

            // Trouver la catégorie racine "Products"
            const productsCategory = categories.find(
                cat => cat.Name === 'Products'
                //cat => cat.LocalizedDescription?.toLowerCase() === localizedCategoryName.toLowerCase()
            );
            // console.log('Products category found:', productsCategory);

            if (productsCategory && productsCategory.RecordId) {
                this._productsRootCategory = {
                    RecordId: productsCategory.RecordId,
                    Name: 'Products'
                };
                this.forceUpdate(); // Forcer le re-render
            }
        } catch (error) {
            this.props.context.telemetry.error('Failed to load Products category');
            this.props.context.telemetry.exception(error as Error);
        }
    };

    /**
     * Render Cart Item count.
     * @returns JSX Element.
     */
    public render(): JSX.Element | null {
        const cart = this.props.data.cart.result ?? undefined;
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- check config.
        const shouldShowMiniCart =
            cart?.isProductAddedToCart && this.props.context.app.config.addToCartBehavior === AddToCartBehavior.showMiniCart;
        if (shouldShowMiniCart) {
            if (!this.isAutoDisplayTriggered) {
                // First time trigger auto mini cart.
                this.setState({ isModalOpen: true });
                this.isAutoDisplayTriggered = true;
            } else if (!this.state.isModalOpen) {
                // If modal is closed, reset the flag.
                this.isAutoDisplayTriggered = false;
            }
        }
        return (
            <Node {...this.props.miniCartWrapper} {...this.props.renderModuleAttributes(this.props)}>
                <button onClick={this._openModal} className='ms-cart-icon-wrapper msc-btn' ref={this.cartIconRef}>
                    {this.props.cartIcon}
                </button>
                <Node {...this._modalContainer()}>
                    <Node className='ms-cart-icon__header msc-modal__header'>
                        {this.props.flyoutTitle}
                        {this._renderCartItemCount(this.props)}
                        {this._renderCartCloseIcon()}
                    </Node>
                    <Node className='ms-cart-icon__body msc-modal__body'>
                        {this.props.data.cart.result?.isEmpty ? this.props.slots.emptyCart : null}
                        {this.props.checkoutBlockedDueToUnavailableFunds}
                        <Node {...this.props.CartlinesWrapper}>
                            {renderCartlines(this.props.cartlines, this.props, this._flyoutSalesUnits)}
                        </Node>
                    </Node>
                    <Node className='ms-cart-icon__footer msc-modal__footer'>
                        {this.props.slots.promoContentItem}
                        {renderTotalPrice(this.props)}
                        {renderSubtotalMessage(this.props)}
                        {this.props.checkoutAsSignInUserButton}
                        {this.props.checkoutAsGuestButton}
                        {this.props.data.cart.result?.isEmpty ? null : this.props.goToCartButton}
                        <Node className='ms-cart-icon__btn-section'>
                            {this.props.data.cart.result?.isEmpty ? (
                                <Button className='ms-cart-icon__btn-backtoshopping' href={this._getProductsCategoryUrl()}>
                                    {this.props.resources.continueShoppingButtonTitle}
                                </Button>
                            ) : null}
                        </Node>
                    </Node>
                </Node>
            </Node>
        );
    }

    /**
     * Initiate modal container.
     * @returns Inode props.
     */
    private readonly _modalContainer = (): INodeProps => {
        return {
            tag: Modal,
            placement: 'bottom-end',
            hideArrow: true,
            className: 'ms-cart-icon__minicartmodal-container',
            wrapClassName: 'ms-cart-icon__minicartmodal',
            isOpen: this.state.isModalOpen,
            toggle: this.closeModal
        };
    };

    /**
     * Initiate open modal.
     */
    private readonly _openModal = (): void => {
        const payLoad = getPayloadObject('click', this.props.telemetryContent!, 'cart-icon', '');
        onTelemetryClick(this.props.telemetryContent!, payLoad, 'cart-icon');
        this.setState({
            isModalOpen: true
        });
    };

    /**
     * Initiate close modal.
     */
    private readonly closeModal = (): void => {
        this.cartIconRef.current?.focus();
        this.setState({
            isModalOpen: false
        });
    };

    /**
     * Initiate cart close Icon.
     * @returns JSX Element.
     */
    private readonly _renderCartCloseIcon = (): JSX.Element | null => {
        return <Button className='ms-cart-icon__btn-close' aria-label='Close' onClick={this.closeModal} />;
    };

    /**
     * Render Cart Item count.
     * @param props - Cart icon view props.
     * @returns JSX Element.
     */
    private readonly _renderCartItemCount = (props: ICartIconViewProps & ICartIconExtensionProps<{}>): JSX.Element | null => {
        const defaultCartItemCount: number = 0;

        const cartItemlength = props.data.cart.result ? props.data.cart.result.totalItemsInCart : defaultCartItemCount;
        const cartItemCount = `${cartItemlength} ${props.resources.items}`;

        return <Node className='ms-cart-icon__count'>{cartItemCount}</Node>;
    };

    /**
     * Handle escape click to close modal.
     * @param event - On press of any key.
     */
    private readonly _escFunction = (event: KeyboardEvent) => {
        if (event.keyCode === KeyCodes.Escape && this.state.isModalOpen) {
            this.closeModal();
        }
    };

    /**
     * Get Products Category URL.
     * @returns Products Category URL.
     */
    private readonly _getProductsCategoryUrl = (): string => {
        if (this._productsRootCategory) {
            return (
                getUrlSync('category', this.props.context.actionContext, {
                    categories: [
                        {
                            Name: this._productsRootCategory.Name,
                            RecordId: this._productsRootCategory.RecordId,
                            NeutralizedName: this._productsRootCategory.Name.toLowerCase()
                        }
                    ]
                }) || '/'
            );
        }

        return getUrlSync('home', this.props.context.actionContext) || '/';
    };
}

export default CartIconView;
